import {
  Alert,
  Button,
  CircularProgress,
  Stack,
  Typography,
} from "@mui/material";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Offer } from "../../api/AlqemiDataService";
import SearchBar from "../../common/components/layout/SearchBar";
import { FilterValue } from "../../common/components/table/ColumnFilter";
import { ItemRow } from "../../common/components/table/Row";
import Table from "../../common/components/table/Table";
import { RatingScoreBox } from "../../common/styles/Box.styles";
import { iconGap, tinyGap } from "../../styles/consts";
import { formatDateFromDatabase } from "../../utils";
import MatchStatus from "../components/MatchStatus";
import { useAlqemiArchiveDashboard } from "../context/AlqemiArchiveDashboardProvider";
import ArchiveCandidateFilterDropdown from "./ArchiveCandidateFilterDropdown";

const saveSortName = "archiveOffersSorting";
const savePageNum = "alqemiArchiveOffersPage";
const saveFilterName = "alqemiArchiveOffersFilter";
export default function AlqemiArchiveDashboard({ basePath }) {
  const navigate = useNavigate();
  const {
    offers,
    candidates,
    currentFilters,
    addAlqemiFilter,
    removeAlqemiFilter,
    loadingData,
    loadMore,
    hasMore,
    loadingMore,
    scanLimitReached,
  } = useAlqemiArchiveDashboard();
  const previouslySavedSorting = localStorage.getItem(saveSortName);
  const defaultSorting = previouslySavedSorting
    ? JSON.parse(previouslySavedSorting)
    : {
        param: "created_at",
        order: "desc",
      };
  const [searchKey, setSearchKey] = useState(0);
  const savedPage = localStorage.getItem(savePageNum) || "1";
  const [alqemiPageNum, setAlqemiPageNum] = useState(
    savedPage ? parseInt(savedPage) : 1,
  );

  const dateRangeFilter = currentFilters.find((f) => f.field === "dateRange");
  const hasDateRange = !!(
    dateRangeFilter?.value?.startDate || dateRangeFilter?.value?.endDate
  );

  const handleColumnFilterChange = (
    param: string,
    value: FilterValue | null,
  ) => {
    if (param === "created_at") {
      if (value && value.type === "date" && value.value) {
        const dateRange = value.value as { min: Date | null; max: Date | null };
        addAlqemiFilter({
          field: "dateRange",
          value: {
            startDate: dateRange.min ? dateRange.min.toISOString() : null,
            endDate: dateRange.max ? dateRange.max.toISOString() : null,
          },
        });
      } else {
        removeAlqemiFilter("dateRange");
        removeAlqemiFilter("searchQuery");
      }
    }
  };

  const filteredOffers = (() => {
    const showOnlyTopOffersFilter = currentFilters.find(
      (f) => f.field === "showOnlyTopOffers",
    );
    const currentCandidateFilter = currentFilters.find(
      (f) => f.field === "currentCandidate",
    );
    const showOnlyTop =
      showOnlyTopOffersFilter === undefined
        ? true
        : showOnlyTopOffersFilter.value !== false;

    let result = offers;

    if (
      currentCandidateFilter &&
      currentCandidateFilter.value !== "All candidates"
    ) {
      result = result.filter(
        (offer) =>
          offer.matches &&
          offer.matches.some(
            (match) => match.consultant_id === currentCandidateFilter.value,
          ),
      );
    }

    if (showOnlyTop) {
      if (
        currentCandidateFilter &&
        currentCandidateFilter.value !== "All candidates"
      ) {
        result = result.filter(
          (offer) =>
            offer.matches &&
            offer.matches.some(
              (match) =>
                match.consultant_id === currentCandidateFilter.value &&
                match.score > 5,
            ),
        );
      } else {
        result = result.filter(
          (offer) =>
            offer.matches && offer.matches.some((match) => match.score > 5),
        );
      }
    }

    return result;
  })();

  const handleGoToOffer = async (offer: Offer, candidateEmail?: string) => {
    const currentCandidateFilter = currentFilters.find(
      (f) => f.field === "currentCandidate",
    );
    let url;
    if (candidateEmail) {
      url = `/alqemi/${offer.id}?currentCandidate=${encodeURIComponent(candidateEmail)}`;
    } else if (currentCandidateFilter) {
      const candidateId = currentCandidateFilter.value;
      const candidate = candidates.find((c) => c.consultant_id === candidateId);
      if (candidate && candidate.email) {
        url = `/alqemi/${offer.id}?currentCandidate=${encodeURIComponent(candidate.email)}`;
      } else {
        url = `/alqemi/${offer.id}`;
      }
    } else {
      url = `/alqemi/${offer.id}`;
    }
    navigate(url);
  };

  const getOfferRow = (
    offer: Offer,
    consultantIdFilter?: string,
  ): ItemRow[] => {
    function ClickableStack({
      children,
      candidateEmail,
    }: {
      children: JSX.Element[] | JSX.Element;
      candidateEmail?: string;
    }) {
      const offerUrl = candidateEmail
        ? `/alqemi/${offer.id}?currentCandidate=${encodeURIComponent(candidateEmail)}`
        : `/alqemi/${offer.id}`;

      return (
        <a
          href={offerUrl}
          onClick={(e) => {
            e.preventDefault();
            handleGoToOffer(offer, candidateEmail);
          }}
          target="_self"
          rel="noopener noreferrer"
          style={{
            textDecoration: "none",
            color: "inherit",
            display: "flex",
            width: "100%",
          }}
        >
          <Stack
            sx={{
              width: "100%",
              height: "100%",
              cursor: "crosshair",
              display: "flex",
              justifyContent: "center",
            }}
          >
            {children}
          </Stack>
        </a>
      );
    }

    return [
      {
        sortParam: "title",
        label: "Offer",
        component: (
          <ClickableStack>
            <Typography>{offer.title || "Offer title"}</Typography>
          </ClickableStack>
        ),
        width: "35%",
        minWidth: "7rem",
        filterConfig: {
          type: "text",
        },
      },
      {
        sortParam: "status",
        label: "Status",
        component: (
          <ClickableStack>
            <Stack sx={{ gap: iconGap }}>
              {offer.matches && offer.matches.length > 0 ? (
                consultantIdFilter &&
                consultantIdFilter !== "All candidates" ? (
                  (() => {
                    const match = offer.matches.find(
                      (m) => m.consultant_id === consultantIdFilter,
                    );
                    if (!match) return null;

                    // "sent", "rejected", "timed_out" are terminal statuses that should not be overridden
                    let displayStatus = match.status;
                    if (
                      match.status !== "sent" &&
                      match.status !== "rejected" &&
                      match.status !== "timed_out"
                    ) {
                      if (match.supervisor_confirmed) {
                        displayStatus = "approved";
                      } else if (match.consultant_confirmed) {
                        displayStatus =
                          match.supervisor_enabled_at_confirmation === false
                            ? "approved"
                            : "supervisor_review";
                      }
                    }
                    // Handle legacy "viewed" status
                    if (displayStatus === "viewed") {
                      displayStatus = "consultant_review";
                    }

                    return (
                      <MatchStatus
                        key={match.consultant_id}
                        status={displayStatus}
                      />
                    );
                  })()
                ) : (
                  (() => {
                    if (!offer.sortedMatches) {
                      offer.sortedMatches = [...offer.matches].sort((a, b) => {
                        const findCandidate = (match) =>
                          candidates.find(
                            (c) => c.consultant_id === match.consultant_id,
                          ) || { name: "", email: "" };

                        const candidateA = findCandidate(a);
                        const candidateB = findCandidate(b);

                        return candidateA.name.localeCompare(candidateB.name);
                      });
                    }

                    return offer.sortedMatches.map((match, index) => {
                      // Trust the backend status; only remap the legacy "viewed" value.
                      const displayStatus =
                        match.status === "viewed"
                          ? "consultant_review"
                          : match.status;

                      return <MatchStatus key={index} status={displayStatus} />;
                    });
                  })()
                )
              ) : (
                <MatchStatus status={"pending"} />
              )}
            </Stack>
          </ClickableStack>
        ),
        width: "15%",
        minWidth: "6rem",
        filterConfig: {
          type: "select",
          options: [
            { value: "matched", name: "Matched" },
            { value: "consultant_review", name: "Consultant review" },
            { value: "supervisor_review", name: "Supervisor review" },
            { value: "approved", name: "Approved" },
            { value: "sent", name: "Sent" },
            { value: "rejected", name: "Rejected" },
            { value: "timed_out", name: "Timed out" },
          ],
        },
      },
      {
        sortParam: "matches.score",
        label: "Candidate's rating",
        component: (
          <Stack sx={{ gap: tinyGap, width: "100%" }}>
            {offer.matches &&
              (consultantIdFilter && consultantIdFilter !== "All candidates"
                ? (() => {
                    const match = offer.matches.find(
                      (m) => m.consultant_id === consultantIdFilter,
                    );
                    if (!match) return null;

                    const candidate = candidates.find(
                      (c) => c.consultant_id === match.consultant_id,
                    ) || { name: "", email: "" };

                    const responsiveWidthStyles = {
                      "@media (min-width: 1000px) and (max-width: 1300px)": {
                        width: "70%",
                      },
                      "@media (min-width: 1300px) and (max-width: 2300px)": {
                        width: "60%",
                      },
                      "@media (min-width: 2300px)": {
                        width: "40%",
                      },
                    };

                    return (
                      <ClickableStack candidateEmail={candidate.email}>
                        <Stack
                          sx={{
                            flexDirection: "row",
                            ...responsiveWidthStyles,
                            maxGap: tinyGap,
                            justifyContent: "space-between",
                          }}
                        >
                          <Typography>{candidate.name}</Typography>
                          <RatingScoreBox>{match.score}</RatingScoreBox>
                        </Stack>
                      </ClickableStack>
                    );
                  })()
                : (() => {
                    const sortedMatches = [...offer.matches].sort((a, b) => {
                      const findCandidate = (match) =>
                        candidates.find(
                          (c) => c.consultant_id === match.consultant_id,
                        ) || { name: "", email: "" };

                      const candidateA = findCandidate(a);
                      const candidateB = findCandidate(b);

                      return candidateA.name.localeCompare(candidateB.name);
                    });
                    offer.sortedMatches = sortedMatches;

                    return sortedMatches.map((match, idx) => {
                      const candidate = candidates.find(
                        (c) => c.consultant_id === match.consultant_id,
                      ) || { name: "", email: "" };

                      const responsiveWidthStyles = {
                        "@media (min-width: 1000px) and (max-width: 1300px)": {
                          width: "70%",
                        },
                        "@media (min-width: 1300px) and (max-width: 2300px)": {
                          width: "60%",
                        },
                        "@media (min-width: 2300px)": {
                          width: "40%",
                        },
                      };
                      return (
                        <ClickableStack
                          key={idx}
                          candidateEmail={candidate.email}
                        >
                          <Stack
                            sx={{
                              flexDirection: "row",
                              ...responsiveWidthStyles,
                              maxGap: tinyGap,
                              justifyContent: "space-between",
                            }}
                          >
                            <Typography>{candidate.name}</Typography>
                            <RatingScoreBox>{match.score}</RatingScoreBox>
                          </Stack>
                        </ClickableStack>
                      );
                    });
                  })())}
          </Stack>
        ),
        width: "25%",
        minWidth: "6rem",
        filterConfig: {
          includeGlobalFilter: true,
          type: "number",
          min: 0,
          max: 10,
        },
      },
      {
        sortParam: "created_at",
        label: "Date created",
        component: (
          <ClickableStack>
            <Typography>
              {formatDateFromDatabase(offer.created_at, true)}
            </Typography>
          </ClickableStack>
        ),
        width: "20%",
        minWidth: "6rem",
        filterConfig: {
          type: "date",
        },
      },
    ];
  };

  return (
    <Stack
      sx={{
        height: "100%",
        width: "100%",
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
      }}
    >
      <SearchBar
        type="archived-offers"
        disabled={!hasDateRange}
        disabledMessage="Set a date range filter first to enable search"
        onSearch={async (searchQuery) => {
          if (searchQuery) {
            addAlqemiFilter({ field: "searchQuery", value: searchQuery });
          } else {
            removeAlqemiFilter("searchQuery");
          }
          setAlqemiPageNum(1);
          setSearchKey((prev) => prev + 1);
        }}
        setLoadingData={() => {}}
      />
      {scanLimitReached && (
        <Alert severity="info" sx={{ borderRadius: 0 }}>
          {`Showing offers from ${
            dateRangeFilter?.value?.startDate
              ? new Date(dateRangeFilter.value.startDate).toLocaleDateString()
              : "the beginning"
          } to ${
            dateRangeFilter?.value?.endDate
              ? new Date(dateRangeFilter.value.endDate).toLocaleDateString()
              : "now"
          }. Results outside this period will not be displayed.`}
        </Alert>
      )}
      {filteredOffers.length === 0 && !loadingData ? (
        <Stack
          sx={{
            height: "100%",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            padding: "2rem",
          }}
        >
          <Typography variant="h6" color="text.secondary">
            {offers.length > 0
              ? "No offers with top matches (score > 5)"
              : "No archived offers available"}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            {offers.length > 0
              ? "Try clicking 'Show all matches' to see all offers"
              : "Archived offers will appear here once they are moved from the main dashboard"}
          </Typography>
        </Stack>
      ) : (
        <>
          <Table
            key={searchKey}
            filters={[]}
            hideDropdown={true}
            items={filteredOffers}
            idProperty="id"
            deleteAllFunction={() => {}}
            filterFunction={() => filteredOffers}
            getItemRow={getOfferRow}
            defaultSort={defaultSorting}
            saveSortName={saveSortName}
            savePageNum={savePageNum}
            loading={loadingData}
            saveFilterName={saveFilterName}
            upperStateForPage={alqemiPageNum}
            setUpperStateForPage={setAlqemiPageNum}
            onColumnFilterChange={handleColumnFilterChange}
            filteringArea={
              <Stack
                sx={{
                  flexDirection: "row",
                  justifyContent: "space-between",
                  width: "100%",
                }}
              >
                <Button
                  variant="outlined"
                  id={"top-offers-button"}
                  onClick={async () => {
                    setSearchKey((prev) => prev + 1);
                    const showOnlyTopOffers = currentFilters.find(
                      (f) => f.field === "showOnlyTopOffers",
                    );
                    if (showOnlyTopOffers === undefined) {
                      addAlqemiFilter({
                        field: "showOnlyTopOffers",
                        value: false,
                      });
                      setAlqemiPageNum(1);
                    } else if (showOnlyTopOffers.value === false) {
                      removeAlqemiFilter("showOnlyTopOffers");
                      setAlqemiPageNum(1);
                    }
                  }}
                >
                  {(() => {
                    const filter = currentFilters.find(
                      (f) => f.field === "showOnlyTopOffers",
                    );
                    const isShowingAll = filter && filter.value === false;
                    return isShowingAll
                      ? "Show only top matches"
                      : "Show all matches";
                  })()}
                </Button>

                <ArchiveCandidateFilterDropdown
                  candidates={candidates}
                  setPageNum={setAlqemiPageNum}
                  saveFilterName={saveFilterName}
                />
              </Stack>
            }
          />
          {hasMore && (
            <Stack
              sx={{ alignItems: "center", py: 2, paddingBottom: "1.25rem" }}
            >
              <Button
                variant="outlined"
                onClick={loadMore}
                disabled={loadingMore}
                startIcon={
                  loadingMore ? <CircularProgress size={16} /> : undefined
                }
              >
                {loadingMore
                  ? "Loading..."
                  : `Load more archived offers (${offers.length} loaded)`}
              </Button>
            </Stack>
          )}
        </>
      )}
    </Stack>
  );
}
